import { Suspense } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { useDensityPreference, DensityProvider } from "../density";
import CommandPalette from "./CommandPalette";
import DensityControl from "./DensityControl";
import { LedgerEventsProvider } from "./LedgerEventsProvider";
import PresenceBar from "./PresenceBar";
import { PresenceProvider } from "./PresenceProvider";
import GlyphLegend from "./GlyphLegend";
import { SHORTCUT_LEGEND, useKeyboardShortcuts } from "../hooks/useKeyboardShortcuts";

// Four destinations: the four jobs, not the eight screens.
const NAV = [
  { to: "/", label: "Now", end: true },
  { to: "/runs", label: "Runs", end: false },
  { to: "/coverage", label: "Coverage", end: false },
  { to: "/policies", label: "Policies", end: false },
] as const;

function NavItems({ className }: { className?: string }) {
  return (
    <>
      {NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            `${className ?? "btn btn-ghost btn-sm"} ${isActive ? "btn-active" : ""}`
          }
        >
          {item.label}
        </NavLink>
      ))}
    </>
  );
}

export default function Layout() {
  const { user, signOut, authRequired } = useAuth();
  const { helpOpen, setHelpOpen, paletteOpen, setPaletteOpen } = useKeyboardShortcuts();
  const [density, setDensity] = useDensityPreference();

  return (
    <DensityProvider value={density}>
      <PresenceProvider>
        <div className="min-h-screen bg-base-100">
          <a
            href="#main"
            className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-2 focus:left-2 btn btn-sm btn-primary"
          >
            Skip to content
          </a>
          <div className="navbar bg-base-200 border-b border-base-300">
            <div className="flex-1 gap-2 px-2 items-center">
              <NavLink to="/" className="font-bold text-lg tracking-tight">
                Run Ledger
              </NavLink>
              <div className="dropdown md:hidden">
                <div tabIndex={0} role="button" className="btn btn-ghost btn-sm">
                  Menu
                </div>
                <ul
                  tabIndex={0}
                  className="dropdown-content menu bg-base-100 rounded-box z-20 mt-2 w-52 p-2 shadow"
                >
                  {NAV.map((item) => (
                    <li key={item.to}>
                      <NavLink to={item.to} end={item.end}>
                        {item.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="hidden md:flex gap-1">
                <NavItems />
              </div>
              <button
                type="button"
                className="hidden md:inline-flex btn btn-ghost btn-sm gap-2 opacity-70"
                onClick={() => setPaletteOpen(true)}
                aria-label="Open command palette"
              >
                Search
                <kbd className="kbd kbd-sm">⌘K</kbd>
              </button>
            </div>
            <div className="flex-none gap-3 px-2">
              <PresenceBar />
              <DensityControl value={density} onChange={setDensity} />
              <span className="text-sm opacity-70 hidden sm:inline">{user?.email ?? "signed in"}</span>
              {authRequired && (
                <button type="button" className="btn btn-ghost btn-sm" onClick={signOut}>
                  Sign out
                </button>
              )}
            </div>
          </div>
          <LedgerEventsProvider>
            <main id="main" className="p-4 md:p-6 max-w-[90rem] mx-auto">
              <Suspense
                fallback={
                  <div className="flex justify-center p-8">
                    <span className="loading loading-spinner loading-lg" />
                  </div>
                }
              >
                <Outlet />
              </Suspense>
            </main>
          </LedgerEventsProvider>
          <dialog className={`modal ${helpOpen ? "modal-open" : ""}`} aria-label="Keyboard shortcuts and legend">
            <div className="modal-box max-w-3xl">
              <h2 className="font-bold text-lg mb-3">Keyboard shortcuts</h2>
              <ul className="space-y-2 text-sm">
                {SHORTCUT_LEGEND.map((row) => (
                  <li key={row.keys} className="flex justify-between gap-4">
                    <kbd className="kbd kbd-sm">{row.keys}</kbd>
                    <span>{row.action}</span>
                  </li>
                ))}
              </ul>
              <h2 className="font-bold text-lg mt-6 mb-3">Visual vocabulary</h2>
              <GlyphLegend />
              <div className="modal-action">
                <button type="button" className="btn btn-sm" onClick={() => setHelpOpen(false)}>
                  Close
                </button>
              </div>
            </div>
            <button
              type="button"
              className="modal-backdrop"
              aria-label="Close shortcuts"
              onClick={() => setHelpOpen(false)}
            />
          </dialog>
          {paletteOpen && <CommandPalette onClose={() => setPaletteOpen(false)} />}
        </div>
      </PresenceProvider>
    </DensityProvider>
  );
}
